import classNames from 'classnames';
import type React from 'react';
import { useLocation } from 'react-router-dom';

import { RevelioTooltip } from '@revelio/ui';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import previewStyles from '../../components/SavedViews/saved-view-preview.module.css';
import { shouldShowCurrentPreviousToggle } from '../config/currentPreviousConfig';
import { useFilterContext } from '../contexts/FilterProvider';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilterInstance,
  isSegmentedFilter,
} from '../types/filters';
import { isMaxEffectivelySet } from '../utils/common';
import { getSegmentSelectionNames } from '../utils/filterSegmentation';
import { ChipCloseButton } from './ChipCloseButton';
import { FilterPopover } from './FilterPopover';
import type { FilterTagProps } from './FilterTag';
import styles from './filter-chip.module.css';

const getSegmentsText = (
  segmentations: Record<string, Filter> | undefined
): string => {
  const allSelections = getSegmentSelectionNames(segmentations);

  if (allSelections.length === 0) return 'All Employees';
  if (allSelections.length > 2) {
    const first2Segments = allSelections.slice(0, 2).join(', ');
    const remainingSegmentsCount = allSelections.length - 2;
    const otherText = remainingSegmentsCount === 1 ? 'other' : 'others';

    return `${first2Segments}, and ${remainingSegmentsCount} ${otherText}`;
  }

  return allSelections.join(', ');
};

const getPrefix = (filterId: FilterId, currencySymbol: string): string => {
  const currencyFilters = [
    FilterId.SALARY,
    FilterId.LAST_FUNDING_AMOUNT,
    FilterId.TOTAL_FUNDING_AMOUNT,
  ];
  return currencyFilters.includes(filterId) ? currencySymbol : '';
};

export const SubFilterTag: React.FC<FilterTagProps> = ({
  filter,
  onRemoveFilter,
  disabled = false,
  hideDisabledTooltip = false,
  isPreview = false,
}) => {
  const { exchangeRate, currencySymbol } = useCurrencyPreference();
  const { dispatch } = useFilterContext();
  const location = useLocation();

  const filterToRender = isSegmentedFilter(filter)
    ? filter.wrappedFilter
    : filter;

  const handleSubfilterClear = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isMultiFilterInstance(filter)) {
      dispatch({
        type: 'REMOVE_MULTI_FILTER_INSTANCE',
        payload: {
          parentId: filter.parentMultiFilterId,
          instanceIndex: filter.instanceIndex,
        },
      });
      return;
    }
    onRemoveFilter(filter.id);
  };

  const segmentsText = isSegmentedFilter(filter)
    ? getSegmentsText(filter.segmentations)
    : 'All Employees';

  const values = (() => {
    if (filterToRender.type !== FilterType.RANGE) {
      return null;
    }

    // Range filters should always have min and max properties (even if undefined)
    // but after URL deserialization they might be missing, so we handle that gracefully
    const min = 'min' in filterToRender ? filterToRender.min : undefined;
    const max = 'max' in filterToRender ? filterToRender.max : undefined;

    const isRateFilter =
      filterToRender.id === FilterId.ATTRITION_RATE ||
      filterToRender.id === FilterId.HIRING_RATE ||
      filterToRender.id === FilterId.GROWTH_RATE;
    const isCurrencyFilter =
      filterToRender.id === FilterId.SALARY ||
      filterToRender.id === FilterId.LAST_FUNDING_AMOUNT ||
      filterToRender.id === FilterId.TOTAL_FUNDING_AMOUNT;

    const startValue =
      isCurrencyFilter && min !== undefined ? min * exchangeRate : min;
    const endValue = (() => {
      if (max === undefined) return undefined;
      return isCurrencyFilter ? max * exchangeRate : max;
    })();

    const prefix = getPrefix(filterToRender.id, currencySymbol);
    const suffix = isRateFilter ? '%' : '';
    const formattedStartValue = `${prefix}${startValue}${suffix}`;
    const formattedEndValue = `${prefix}${endValue}${suffix}`;

    const isNumber = (value: number | undefined): value is number =>
      typeof value === 'number';

    const isMinSet = min !== undefined;
    const maxEffectivelySet = isMaxEffectivelySet(
      max,
      filterToRender.id,
      isMinSet
    );

    // Only show "from X to Y" if both values exist AND max is effectively set (not a default)
    if (isNumber(startValue) && isNumber(endValue) && maxEffectivelySet) {
      return (
        <span className={styles.inlineRow}>
          <span>from</span>
          <span className={styles.value}>{formattedStartValue}</span>
          <span>to</span>
          <span className={styles.value}>{formattedEndValue}</span>
        </span>
      );
    }

    // If only startValue exists (greater than case), show "is greater than"
    if (isNumber(startValue)) {
      return (
        <span className={styles.inlineRow}>
          <span>is greater than</span>
          <span className={styles.value}>{formattedStartValue}</span>
        </span>
      );
    }

    if (isNumber(endValue)) {
      return (
        <span className={styles.inlineRow}>
          <span>is less than</span>
          <span className={styles.value}>{formattedEndValue}</span>
        </span>
      );
    }

    return null;
  })();

  if (filterToRender.type === FilterType.RANGE && !values) {
    return null;
  }

  const filterChipName = (() => {
    const shouldShowCurrentPrevious = shouldShowCurrentPreviousToggle(
      filter.id,
      location.pathname
    );

    const name = filter.name;

    if (!shouldShowCurrentPrevious || !('isCurrent' in filter)) {
      return name;
    }

    const prefix = filter.isCurrent ? 'Current' : 'Previous';
    return `${prefix} ${name}`;
  })();

  const renderTag = ({ isDisabled }: { isDisabled: boolean }) => {
    return (
      <span
        className={classNames(
          styles.chip,
          styles.bgSubtle,
          isDisabled && styles.disabled,
          isPreview && previewStyles.previewChip
        )}
        data-testid={
          isPreview
            ? 'saved-view-preview-filter-chip'
            : `filter-chip-${filter.id}`
        }
      >
        <span className={styles.chipRow}>
          <span className={styles.chipLabel}>
            <span className={styles.row}>
              <span className={styles.title}>{filterChipName}</span>
              <span className={styles.row}>
                <span>• for </span>
                <span className={styles.value}>{segmentsText}</span>
                <span>•</span>
              </span>
              {values}
            </span>
          </span>
          {!isPreview && <ChipCloseButton onClick={handleSubfilterClear} />}
        </span>
      </span>
    );
  };

  const renderTrigger = (() => {
    if (disabled) {
      return (
        <RevelioTooltip
          label="This filter is unavailable on this page."
          openDelay={375}
          isDisabled={hideDisabledTooltip}
        >
          {renderTag({ isDisabled: true })}
        </RevelioTooltip>
      );
    }

    return renderTag({ isDisabled: false });
  })();

  if (isPreview) {
    return values !== null && renderTag({ isDisabled: false });
  }

  return (
    values !== null && (
      <FilterPopover
        filter={filter}
        trigger={<span className={styles.chipTrigger}>{renderTrigger}</span>}
      />
    )
  );
};
